import type { ColumnDef } from "@tanstack/react-table";
import CopyButton from "@/components/CopyButton";
import Icon from "@/components/Icon";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import {
  importWarningDetailValues,
  PersonOverviewLink,
  resolvedImportWarningDetails,
  type ImportWarningDetailValue
} from "../_utils";
import type { EligibleStudent } from "../types";

type RosterImportWarningRow = {
  id: string;
  message: string;
  details: ImportWarningDetailValue[];
};

function WarningDetailValues({
  warningId,
  details,
  shouldShowPersonProfileActions
}: {
  warningId: string;
  details: ImportWarningDetailValue[];
  shouldShowPersonProfileActions: boolean;
}) {
  if (details.length === 0) {
    return <span className="text-sys-brand-on-surface-variant">—</span>;
  }

  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {details.map((detail) => (
        <li key={detail.key} className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 text-label-small text-sys-brand-on-surface-variant">{detail.label}</span>
          {shouldShowPersonProfileActions && detail.href ? (
            <PersonOverviewLink
              pgaId={detail.value}
              className="min-w-0 truncate text-body-medium text-sys-brand-primary hover:underline"
            />
          ) : (
            <span className="min-w-0 truncate text-body-medium text-sys-brand-on-surface">{detail.value}</span>
          )}
          {detail.copyable && (
            <CopyButton
              textToCopy={detail.value}
              copyFieldLabel={detail.copyFieldLabel}
              className="shrink-0"
              testId={`copy-warning-detail-${warningId}-${detail.key}`}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

function warningRows(student: EligibleStudent): RosterImportWarningRow[] {
  return (student.importWarnings ?? []).map((warning, index) => ({
    id: `${student.id}-${warning.field}-${index}`,
    message: warning.message,
    details: importWarningDetailValues(resolvedImportWarningDetails(warning, student))
  }));
}

function warningColumns(shouldShowPersonProfileActions: boolean): ColumnDef<RosterImportWarningRow>[] {
  return [
    {
      accessorKey: "message",
      header: "Warning",
      cell: ({ row }) => (
        <div className="flex items-start gap-2">
          <Icon name="warning" size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <span className="text-body-medium">{row.original.message}</span>
        </div>
      )
    },
    {
      id: "values",
      header: "Values",
      cell: ({ row }) => (
        <WarningDetailValues
          warningId={row.original.id}
          details={row.original.details}
          shouldShowPersonProfileActions={shouldShowPersonProfileActions}
        />
      )
    }
  ];
}

export default function RosterImportWarningsPanel({
  student,
  shouldShowPersonProfileActions
}: {
  student: EligibleStudent;
  shouldShowPersonProfileActions: boolean;
}) {
  const rows = warningRows(student);

  return (
    <div data-testid={`roster-warnings-${student.id}`} onClick={(event) => event.stopPropagation()}>
      <TableDetailsPanel
        data={rows}
        columns={warningColumns(shouldShowPersonProfileActions)}
        title={`Warnings (${rows.length})`}
      />
    </div>
  );
}
